import React from 'react'
import styled from '@emotion/styled'
import { Button, Card, Dropdown, Input, Modal, Space } from 'antd'
import type { MenuProps } from 'antd'
import { DeleteOutlined, EllipsisOutlined, PlusOutlined } from '@ant-design/icons'
import {
  PortalFaqAnswerBlock,
  PortalFaqConfig,
  PortalRichText,
} from '../../../models/PortalArtigoModel'
import { createDefaultFaq } from '../helpers/artigo-defaults'
import { getPortalEditorFaqItemId } from '../helpers/editor-ids'
import {
  formatCount,
  getRichTextPlainText,
  hasRichTextContent,
} from '../helpers/rich-text-utils'
import { PortalEditorField } from './PortalEditorField'
import {
  CompactSummary,
  SummaryBadges,
  SummaryBadge,
  SummaryDescription,
  SummaryMain,
  SummaryTitle,
} from './PortalCompactSummary'
import { RichTextField, RichTextListEditor } from './PortalRichTextFields'

const { TextArea } = Input

interface FaqEditorProps {
  value?: PortalFaqConfig
  onChange: (value: PortalFaqConfig) => void
  editingQuestionIndex: number | null
  onEditingQuestionChange: (index: number | null) => void
}

export function FaqEditor({
  value,
  onChange,
  editingQuestionIndex,
  onEditingQuestionChange,
}: FaqEditorProps) {
  const faq = value || createDefaultFaq()
  const updateFaq = (patch: Partial<PortalFaqConfig>) => onChange({ ...faq, ...patch })
  const items = faq.items || []

  const updateFaqItem = (
    index: number,
    patch: Partial<PortalFaqConfig['items'][number]>
  ) => {
    updateFaq({
      items: items.map((current, currentIndex) =>
        currentIndex === index ? { ...current, ...patch } : current
      ),
    })
  }

  const removeFaqItem = (index: number) => {
    updateFaq({ items: items.filter((_, currentIndex) => currentIndex !== index) })
    if (editingQuestionIndex === index) onEditingQuestionChange(null)
    if (typeof editingQuestionIndex === 'number' && editingQuestionIndex > index) {
      onEditingQuestionChange(editingQuestionIndex - 1)
    }
  }

  const addFaqItem = () => {
    updateFaq({
      items: [...items, { id: getNextFaqItemId(items), question: '', answerText: '', answer: [] }],
    })
    onEditingQuestionChange(items.length)
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <PortalEditorField label="Título do FAQ">
        <Input
          value={faq.title}
          placeholder="Título"
          onChange={(event) => updateFaq({ title: event.target.value })}
        />
      </PortalEditorField>
      {items.map((item, index) => {
        const isEditing = editingQuestionIndex === index
        const moreActions = getFaqQuestionActionItems({
          onRemove: () => removeFaqItem(index),
        })

        return (
          <Card
            id={getPortalEditorFaqItemId(index)}
            key={index}
            size="small"
            title={`Pergunta ${index + 1}`}
            extra={
              <Space>
                <Button
                  size="small"
                  onClick={() => onEditingQuestionChange(isEditing ? null : index)}
                >
                  {isEditing ? 'Recolher' : 'Editar'}
                </Button>
                <Dropdown menu={{ items: moreActions }} trigger={['click']}>
                  <Button size="small" icon={<EllipsisOutlined />}>
                    Mais ações
                  </Button>
                </Dropdown>
              </Space>
            }
          >
            {!isEditing && <FaqQuestionSummary item={item} index={index} />}
            {isEditing && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <PortalEditorField label="Pergunta">
                  <Input
                    value={item.question}
                    placeholder="Pergunta"
                    onChange={(event) => updateFaqItem(index, { question: event.target.value })}
                  />
                </PortalEditorField>
                <PortalEditorField label="Resposta SEO">
                  <TextArea
                    value={item.answerText}
                    placeholder="Resposta SEO"
                    autoSize
                    onChange={(event) => updateFaqItem(index, { answerText: event.target.value })}
                  />
                </PortalEditorField>
                <AdvancedFields>
                  <summary>Configurações avançadas</summary>
                  <PortalEditorField label="ID da pergunta">
                    <Input
                      value={item.id}
                      placeholder="ID"
                      onChange={(event) => updateFaqItem(index, { id: event.target.value })}
                    />
                  </PortalEditorField>
                </AdvancedFields>
                <RichTextListEditor
                  label="Resposta"
                  value={getFaqParagraphs(item.answer)}
                  onChange={(answerItems) =>
                    updateFaqItem(index, {
                      answer: mergeFaqParagraphsWithExistingAnswer(item.answer, answerItems),
                    })
                  }
                />
                {getFaqListBlocksCount(item.answer) > 0 ? (
                  <ReadOnlyHelper>
                    Esta resposta possui {getFaqListBlocksCount(item.answer)} lista(s)
                    preservada(s). A edição visual completa de listas fica para outra etapa.
                  </ReadOnlyHelper>
                ) : null}
              </Space>
            )}
          </Card>
        )
      })}
      <Button icon={<PlusOutlined />} onClick={addFaqItem}>
        Adicionar pergunta
      </Button>
      <AdvancedFields>
        <summary>Configurações avançadas</summary>
        <Space direction="vertical" style={{ width: '100%' }}>
          <PortalEditorField
            label="Âncora do FAQ"
            helper="Usada nos links do índice, por exemplo #faq."
          >
            <Input
              value={faq.anchorId}
              placeholder="faq"
              onChange={(event) => updateFaq({ anchorId: event.target.value })}
            />
          </PortalEditorField>
        </Space>
      </AdvancedFields>
      <PortalEditorField label="URL do WhatsApp">
        <Input
          value={faq.whatsapp?.href}
          placeholder="URL do WhatsApp"
          onChange={(event) => updateFaq({ whatsapp: { ...faq.whatsapp, href: event.target.value } })}
        />
      </PortalEditorField>
      <RichTextField
        label="Descrição WhatsApp"
        value={faq.whatsapp?.description}
        onChange={(description) =>
          updateFaq({ whatsapp: { ...faq.whatsapp, description: description || '' } })
        }
      />
    </Space>
  )
}

function getNextFaqItemId(items: PortalFaqConfig['items']) {
  const usedIds = new Set((items || []).map((item) => item.id).filter(Boolean))
  let nextIndex = 1

  while (usedIds.has(`faq-${nextIndex}`)) {
    nextIndex += 1
  }

  return `faq-${nextIndex}`
}

function getFaqQuestionActionItems({ onRemove }: { onRemove: () => void }): MenuProps['items'] {
  return [
    {
      key: 'remove',
      label: 'Remover',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () =>
        confirmRemoval({
          title: 'Remover pergunta',
          content: 'Tem certeza que deseja remover esta pergunta do FAQ?',
          onConfirm: onRemove,
        }),
    },
  ]
}

function confirmRemoval({
  title,
  content,
  onConfirm,
}: {
  title: string
  content: string
  onConfirm: () => void
}) {
  Modal.confirm({
    title,
    content,
    okText: 'Remover',
    cancelText: 'Cancelar',
    okType: 'danger',
    onOk: onConfirm,
  })
}

function FaqQuestionSummary({
  item,
  index,
}: {
  item: PortalFaqConfig['items'][number]
  index: number
}) {
  const hasAnswer = hasFaqAnswerContent(item.answer)
  const paragraphsCount = getFaqParagraphs(item.answer).length
  const listsCount = getFaqListBlocksCount(item.answer)

  return (
    <CompactSummary>
      <SummaryMain>
        <SummaryTitle>{item.question?.trim() || `Pergunta ${index + 1} sem texto`}</SummaryTitle>
        <SummaryDescription>{getFaqAnswerSummary(item.answer)}</SummaryDescription>
      </SummaryMain>
      <SummaryBadges>
        <SummaryBadge>{hasAnswer ? 'com resposta' : 'sem resposta'}</SummaryBadge>
        <SummaryBadge>{formatCount(paragraphsCount, 'parágrafo', 'parágrafos')}</SummaryBadge>
        {listsCount > 0 ? (
          <SummaryBadge>{formatCount(listsCount, 'lista', 'listas')}</SummaryBadge>
        ) : null}
      </SummaryBadges>
    </CompactSummary>
  )
}

function getFaqParagraphs(answerBlocks: PortalFaqConfig['items'][number]['answer']) {
  return (answerBlocks || []).reduce<PortalRichText[]>((paragraphs, answerBlock) => {
    if (answerBlock.type === 'paragraph') paragraphs.push(answerBlock.content)
    return paragraphs
  }, [])
}

function mergeFaqParagraphsWithExistingAnswer(
  currentAnswer: PortalFaqAnswerBlock[],
  nextParagraphs: PortalRichText[]
): PortalFaqAnswerBlock[] {
  let paragraphIndex = 0
  const merged = (currentAnswer || []).reduce<PortalFaqAnswerBlock[]>((answer, answerBlock) => {
    if (answerBlock.type !== 'paragraph') {
      answer.push(answerBlock)
      return answer
    }

    if (paragraphIndex < nextParagraphs.length) {
      answer.push({
        ...answerBlock,
        content: nextParagraphs[paragraphIndex],
      })
    }
    paragraphIndex += 1
    return answer
  }, [])

  while (paragraphIndex < nextParagraphs.length) {
    merged.push({
      type: 'paragraph',
      content: nextParagraphs[paragraphIndex],
    })
    paragraphIndex += 1
  }

  return merged
}

function hasFaqAnswerContent(answerBlocks: PortalFaqAnswerBlock[] = []) {
  return answerBlocks.some((answerBlock) => {
    if (answerBlock.type === 'paragraph') return hasRichTextContent(answerBlock.content)
    return (answerBlock.items || []).some(hasRichTextContent)
  })
}

function getFaqListBlocksCount(answerBlocks: PortalFaqAnswerBlock[] = []) {
  return answerBlocks.filter((answerBlock) => answerBlock.type === 'list').length
}

function getFaqAnswerSummary(answerBlocks: PortalFaqAnswerBlock[] = []) {
  const text = answerBlocks
    .map((answerBlock) => {
      if (answerBlock.type === 'paragraph') return getRichTextPlainText(answerBlock.content)
      return (answerBlock.items || []).map(getRichTextPlainText).join(' ')
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!text) return 'Resposta ainda não preenchida.'
  return text.length > 180 ? `${text.slice(0, 177)}...` : text
}

const AdvancedFields = styled.details`
  width: 100%;
  color: #595959;

  summary {
    cursor: pointer;
    color: #595959;
    font-weight: 600;
    margin-bottom: 8px;
  }
`

const ReadOnlyHelper = styled.div`
  margin-bottom: 8px;
  color: #8c8c8c;
  font-size: 12px;
`
